import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { unifiedClienteService, unifiedGrupoService, unifiedBemService, unifiedConsultorService } from '@/services/unifiedService';

// Configurações de cache otimizadas
const CACHE_CONFIG = {
  staleTime: 5 * 60 * 1000, // 5 minutos
  gcTime: 10 * 60 * 1000, // 10 minutos (substitui cacheTime no React Query v5)
  refetchOnWindowFocus: false,
  retry: 2,
  retryDelay: 1000
};

export const useContratoFormData = (tenantId?: number | null) => {
  const queryClient = useQueryClient();


  // Query para grupos
  const {
    data: grupos,
    isLoading: loadingGrupos,
    error: errorGrupos
  } = useQuery({
    queryKey: ['grupos', tenantId], // Use o tenantId direto para garantir refetch
    queryFn: async () => {
      const response = await unifiedGrupoService.findAll();
      return response.data || response || [];
    },
    ...CACHE_CONFIG,
    enabled: true
  });

  // Query para bens
  const {
    data: bens,
    isLoading: loadingBens,
    error: errorBens
  } = useQuery({
    queryKey: ['bens', tenantId], // Use o tenantId direto para garantir refetch
    queryFn: async () => {
      const response = await unifiedBemService.findAll();
      return response.data || response || [];
    },
    ...CACHE_CONFIG,
    enabled: true
  });

  // Query para clientes
  const {
    data: clientes,
    isLoading: loadingClientes,
    error: errorClientes
  } = useQuery({
    queryKey: ['clientes'],
    queryFn: async () => {
      const response = await unifiedClienteService.findAll();
      return response.data || response || [];
    },
    staleTime: 2 * 60 * 1000, // 2 minutos para clientes (mais dinâmico)
    gcTime: 5 * 60 * 1000, // 5 minutos
    refetchOnWindowFocus: false,
    retry: 2
  });

  // Query para consultores
  const {
    data: consultores,
    isLoading: loadingConsultores,
    error: errorConsultores
  } = useQuery({
    queryKey: ['consultores'],
    queryFn: async () => {
      const response = await unifiedConsultorService.findAll();
      return response.data || response || [];
    },
    ...CACHE_CONFIG
  });

  // Query para relacionadores (se disponível)
  const {
    data: relacionadores,
    isLoading: loadingRelacionadores,
    error: errorRelacionadores
  } = useQuery({
    queryKey: ['relacionadores'],
    queryFn: async () => {
      // Implementar quando o serviço estiver disponível
      return [];
    },
    ...CACHE_CONFIG,
    enabled: false // Desabilitado até implementar o serviço
  });

  // Estados de loading e erro
  const isLoading = loadingGrupos || loadingBens || loadingClientes || loadingConsultores;
  const hasError = errorGrupos || errorBens || errorClientes || errorConsultores;

  // Log de erros para debug
  if (hasError) {
    console.error('🚨 Erro ao carregar dados do formulário:', {
      errorGrupos: errorGrupos?.message,
      errorBens: errorBens?.message,
      errorClientes: errorClientes?.message,
      errorConsultores: errorConsultores?.message,
      timestamp: new Date().toISOString()
    });
  }

  // Função para invalidar cache
  const invalidateCache = () => {
    queryClient.invalidateQueries({ queryKey: ['grupos'] });
    queryClient.invalidateQueries({ queryKey: ['bens'] });
    queryClient.invalidateQueries({ queryKey: ['clientes'] });
    queryClient.invalidateQueries({ queryKey: ['consultores'] });
    queryClient.invalidateQueries({ queryKey: ['relacionadores'] });
  };

  // Função para pré-carregar dados
  const preloadData = async () => {
    try {
      await Promise.all([
        queryClient.prefetchQuery({
          queryKey: ['grupos'],
          queryFn: async () => {
            const response = await unifiedGrupoService.findAll();
            return response.data || response || [];
          },
          ...CACHE_CONFIG
        }),
        queryClient.prefetchQuery({
          queryKey: ['bens'],
          queryFn: async () => {
            const response = await unifiedBemService.findAll();
            return response.data || response || [];
          },
          ...CACHE_CONFIG
        }),
        queryClient.prefetchQuery({
          queryKey: ['clientes'],
          queryFn: async () => {
            const response = await unifiedClienteService.findAll();
            return response.data || response || [];
          },
          staleTime: 2 * 60 * 1000,
          gcTime: 5 * 60 * 1000
        }),
        queryClient.prefetchQuery({
          queryKey: ['consultores'],
          queryFn: async () => {
            const response = await unifiedConsultorService.findAll();
            return response.data || response || [];
          },
          ...CACHE_CONFIG
        })
      ]);
    } catch (error) {
      console.error('Erro ao pré-carregar dados:', error);
    }
  };

  // Função para buscar cliente por CPF
  const buscarClientePorCPF = async (cpf: string) => {
    if (!clientes) return null;

    const cpfLimpo = cpf.replace(/\D/g, '');
    return clientes.find((cliente: any) =>
      cliente.cpf && cliente.cpf.replace(/\D/g, '') === cpfLimpo
    );
  };

  // Função para sugerir grupos baseado no perfil do cliente
  const sugerirGrupos = useCallback((cliente: any) => {
    if (!grupos || !cliente) return [];

    // Lógica de sugestão baseada no perfil do cliente
    return grupos
      .map((grupo: any) => ({
        ...grupo,
        score: calcularScoreGrupo(grupo, cliente),
        motivo: gerarMotivoSugestao(grupo, cliente)
      }))
      .sort((a: any, b: any) => b.score - a.score)
      .slice(0, 3);
  }, [grupos]);

  // Função para sugerir bens baseado no valor da parcela
  const sugerirBens = useCallback((valorParcela: number) => {
    if (!bens || !valorParcela) return [];

    return bens
      .map((bem: any) => ({
        ...bem,
        score: calcularScoreBem(bem, valorParcela),
        motivo: gerarMotivoSugestaoBem(bem, valorParcela)
      }))
      .sort((a: any, b: any) => b.score - a.score)
      .slice(0, 3);
  }, [bens]);

  // Funções auxiliares para cálculo de scores
  const calcularScoreGrupo = (grupo: any, cliente: any) => {
    let score = 0;

    // Score baseado no último dígito do CPF (se disponível)
    if (cliente.cpf) {
      const ultimoDigito = parseInt(cliente.cpf.slice(-1));
      if (ultimoDigito <= 4 && grupo.taxa_adm_total <= 5) {
        score += 10;
      } else if (ultimoDigito > 4) {
        score += 5;
      }
    }

    // Score baseado na taxa de administração
    if (grupo.taxa_adm_total <= 3) {
      score += 8;
    } else if (grupo.taxa_adm_total <= 5) {
      score += 5;
    } else {
      score += 2;
    }

    return score;
  };

  const calcularScoreBem = (bem: any, valorParcela: number) => {
    const percentual = (valorParcela / bem.preco_bem) * 100;

    if (percentual >= 1 && percentual <= 5) {
      return 10; // Ideal
    } else if (percentual >= 0.5 && percentual <= 10) {
      return 7; // Aceitável
    } else {
      return 3; // Não recomendado
    }
  };

  const gerarMotivoSugestao = (grupo: any, cliente: any) => {
    const ultimoDigito = cliente.cpf ? parseInt(cliente.cpf.slice(-1)) : null;

    if (ultimoDigito !== null && ultimoDigito <= 4 && grupo.taxa_adm_total <= 5) {
      return 'Taxa baixa ideal para este perfil de cliente';
    } else if (grupo.taxa_adm_total <= 3) {
      return 'Taxa muito baixa - excelente opção';
    } else {
      return 'Grupo com boa relação custo-benefício';
    }
  };

  const gerarMotivoSugestaoBem = (bem: any, valorParcela: number) => {
    const percentual = (valorParcela / bem.preco_bem) * 100;

    if (percentual >= 1 && percentual <= 5) {
      return 'Valor da parcela ideal para este bem';
    } else if (percentual >= 0.5 && percentual <= 10) {
      return 'Valor da parcela adequado para este bem';
    } else {
      return 'Ajuste o valor da parcela para este bem';
    }
  };

  return {
    // Dados
    grupos: grupos || [],
    bens: bens || [],
    clientes: clientes || [],
    consultores: consultores || [],
    relacionadores: relacionadores || [],

    // Estados
    isLoading,
    hasError,
    loadingGrupos,
    loadingBens,
    loadingClientes,
    loadingConsultores,
    loadingRelacionadores,

    // Erros
    errorGrupos,
    errorBens,
    errorClientes,
    errorConsultores,
    errorRelacionadores,

    // Funções
    invalidateCache,
    preloadData,
    buscarClientePorCPF,
    sugerirGrupos,
    sugerirBens
  };
};
